"use client";

import { useRef } from "react";
import type { MouseEvent, ReactNode } from "react";
import type { Accession } from "@/lib/museum/types";
import styles from "./museum.module.css";

const FIELDS: Array<[keyof Accession, string]> = [
  ["made", "Made"],
  ["observed", "Observed"],
  ["medium", "Medium"],
];

export function LedgerMark({ accession }: { accession: Accession }) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  function openEnhancedLedger(event: MouseEvent<HTMLElement>) {
    const dialog = dialogRef.current;
    if (!dialog || typeof dialog.showModal !== "function") return;
    event.preventDefault();
    dialog.showModal();
  }

  return (
    <>
      <details className={styles.ledgerDetails}>
        <summary
          data-ledger-trigger
          onClick={openEnhancedLedger}
          className="absolute bottom-1 left-1 flex min-h-[44px] cursor-pointer list-none items-center rounded-[2px] px-2 font-mono text-[9.5px] tracking-[0.12em] text-[#C9A15C]"
        >
          <span data-ledger-label="open" className="rounded-[2px] bg-[#0A1421]/60 px-2 py-1">
            Open ledger · {accession.number} · {accession.nature.toUpperCase()}
          </span>
          <span data-ledger-label="close" className="rounded-[2px] border border-black/15 bg-[#FBFAF4] px-3 py-2 text-[#6E531F]">
            Close ledger · {accession.number}
          </span>
        </summary>
        <section className={styles.ledgerBaseline} aria-label={`Ledger: ${accession.title}`}>
          <LedgerContent accession={accession} />
        </section>
      </details>
      <dialog
        ref={dialogRef}
        className="m-auto w-[min(92vw,28rem)] border border-black/15 bg-[#FBFAF4] p-6 text-[#212B40] backdrop:bg-black/40"
        aria-label={`Ledger: ${accession.title}`}
      >
        <LedgerContent accession={accession} />
        <form method="dialog" className="mt-5 text-right">
          <button className="min-h-[44px] border border-black/20 px-4 font-mono text-[11px] uppercase tracking-[0.12em]">
            Close
          </button>
        </form>
      </dialog>
    </>
  );
}

function LedgerContent({ accession }: { accession: Accession }) {
  return (
    <>
      <p className="flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.16em] text-[#6E531F]">
        Ledger <span>{accession.number}</span>
      </p>
      <h3 className="mt-2 font-serif text-xl">{accession.title}</h3>
      <p className="mt-1 text-sm text-[#4A5262]">{accession.caption}</p>
      <dl className="mt-4 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-[12.5px]">
        <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#6E531F]">Visual description</dt>
        <dd>{accession.alt}</dd>
        <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#6E531F]">Nature</dt>
        <dd>{accession.nature}</dd>
        {FIELDS.map(([key, label]) =>
          accession[key] ? (
            <FieldRow key={key} label={label} value={String(accession[key])} />
          ) : null
        )}
        <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#6E531F]">Source</dt>
        <dd>
          {accession.source.status === "verified"
            ? <SourceLink href={accession.source.scanUrl}>{accession.source.institution} · {accession.source.licence}</SourceLink>
            : accession.source.status === "candidate"
              ? (
                  <>
                    {accession.source.scanUrl ? (
                      <SourceLink href={accession.source.scanUrl}>
                        {[accession.source.institution, accession.source.licence].filter(Boolean).join(" · ") || "Candidate source record"}
                      </SourceLink>
                    ) : "Candidate source record"}
                    {accession.source.note ? (
                      <span className="mt-1 block text-[#626A78]">{accession.source.note}</span>
                    ) : null}
                  </>
                )
              : `Pending: ${accession.source.note}`}
        </dd>
        {accession.reviewedBy ? <FieldRow label="Reviewed" value={accession.reviewedBy} /> : null}
      </dl>
    </>
  );
}

function SourceLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="underline underline-offset-2">
      {children}
    </a>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-[#6E531F]">{label}</dt>
      <dd>{value}</dd>
    </>
  );
}
